/**
 * Shared component for rendering field validation errors as a bulleted list.
 * Used across all auth pages for consistent error formatting.
 */
import { Box } from "@mui/material";
import { ReactNode } from "react";

interface FieldErrorListProps {
  errors: string[];
}

/**
 * Renders an array of error messages as a bulleted list for use in TextField helperText.
 * Returns null if there are no errors, allowing MUI's default helperText behavior.
 */
export function FieldErrorList({ errors }: FieldErrorListProps): ReactNode {
  if (errors.length === 0) {
    return null;
  }

  return (
    <Box component="ul" sx={{ margin: 0, paddingLeft: "1.25rem" }}>
      {errors.map((error, index) => (
        <li key={index}>{error}</li>
      ))}
    </Box>
  );
}

/**
 * Helper function to render field errors as helperText prop value.
 * Returns null when no errors, allowing MUI TextField to show no helper text.
 *
 * Usage:
 *   helperText={renderFieldErrors(errors)}
 */
export function renderFieldErrors(errors: string[]): ReactNode {
  return <FieldErrorList errors={errors} />;
}
